<script>
import AutomationActionTeamMessageInput from './AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from './AutomationFileInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MacrosAPI from 'dashboard/api/macros';

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    WootMessageEditor,
    NextButton,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  data() {
    return {
      macroVariables: [],
      variableValues: {},
      loadingVariables: false,
    };
  },
  computed: {
    action_name: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_name;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        if (!this.modelValue) return null;
        return this.modelValue.action_params;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      return this.actionTypes.find(action => action.key === this.action_name)
        .inputType;
    },
    actionInputStyles() {
      return {
        'has-error': this.errorMessage,
        'is-a-macro': this.isMacro,
      };
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    isCustomAttributeAction() {
      return (
        this.action_name === 'update_conversation_custom_attributes' ||
        this.action_name === 'update_contact_custom_attributes'
      );
    },
    selectedAttribute() {
      if (!this.isCustomAttributeAction || !this.action_params) return null;
      const attributeKey = this.action_params.id || this.action_params;
      return this.dropdownValues.find(attr => attr.id === attributeKey);
    },
    attributeValueInput: {
      get() {
        if (!this.action_params) return '';
        return this.action_params.value || '';
      },
      set(value) {
        const payload = this.modelValue || {};
        // Extract just the id and value for backend
        const attributeId = this.action_params.id || this.action_params;
        this.$emit('update:modelValue', {
          ...payload,
          action_params: {
            id: attributeId,
            value: value,
          },
        });
      },
    },
    isExecuteMacroAction() {
      return this.action_name === 'execute_macro';
    },
    selectedMacro() {
      if (!this.isExecuteMacroAction || !this.action_params) return null;
      return this.action_params;
    },
  },
  watch: {
    selectedMacro: {
      async handler(newMacro, oldMacro) {
        if (!newMacro || !newMacro.id) {
          this.macroVariables = [];
          this.variableValues = {};
          return;
        }

        // Only fetch if the macro ID actually changed, not just the object reference
        if (oldMacro && oldMacro.id === newMacro.id) {
          return;
        }

        await this.fetchMacroVariables(newMacro.id);
      },
      immediate: true,
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
      this.macroVariables = [];
      this.variableValues = {};
    },
    async fetchMacroVariables(macroId) {
      this.loadingVariables = true;
      try {
        const response = await MacrosAPI.getVariables(macroId);
        this.macroVariables = response.data.variables || [];

        // Initialize variable values if not already set
        if (this.macroVariables.length > 0) {
          this.macroVariables.forEach(varName => {
            if (!this.variableValues[varName]) {
              this.variableValues[varName] = '';
            }
          });
        }
      } catch (error) {
        console.error('Error fetching macro variables:', error);
        this.macroVariables = [];
      } finally {
        this.loadingVariables = false;
      }
    },
    updateVariableValue(varName, value) {
      this.variableValues[varName] = value;

      // Update action_params to include variables without replacing the macro object
      const payload = this.modelValue || {};
      const currentMacro = this.action_params;

      this.$emit('update:modelValue', {
        ...payload,
        action_params: {
          ...currentMacro,
          variables: { ...this.variableValues },
        },
      });
    },
  },
};
</script>

<template>
  <div class="filter" :class="actionInputStyles">
    <div class="filter-inputs">
      <select
        v-model="action_name"
        class="action__question"
        :class="{ 'full-width': !showActionInput }"
        @change="resetAction()"
      >
        <option
          v-for="attribute in actionTypes"
          :key="attribute.key"
          :value="attribute.key"
        >
          {{ attribute.label }}
        </option>
      </select>
      <div v-if="showActionInput" class="filter__answer--wrap">
        <div v-if="inputType" class="w-full">
          <div
            v-if="inputType === 'search_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="action_params"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
              :option-height="104"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <div
            v-else-if="inputType === 'multi_select'"
            class="multiselect-wrap--small"
          >
            <multiselect
              v-model="action_params"
              track-by="id"
              label="name"
              :placeholder="$t('FORMS.MULTISELECT.SELECT')"
              multiple
              selected-label
              :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
              deselect-label=""
              :max-height="160"
              :options="dropdownValues"
              :allow-empty="false"
              :option-height="104"
            >
              <template #noOptions>
                {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
              </template>
            </multiselect>
          </div>
          <input
            v-else-if="inputType === 'email'"
            v-model="action_params"
            type="email"
            class="answer--text-input"
            :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
          />
          <input
            v-else-if="inputType === 'url'"
            v-model="action_params"
            type="url"
            class="answer--text-input"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <input
            v-else-if="inputType === 'plain_text'"
            v-model="action_params"
            type="text"
            class="answer--text-input"
            :placeholder="$t('FORMS.PLACEHOLDER.ENTER_VALUE')"
          />
          <AutomationActionFileInput
            v-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
        </div>
      </div>
      <NextButton
        v-if="!isMacro"
        icon="i-lucide-x"
        slate
        ghost
        class="flex-shrink-0"
        @click="removeAction"
      />
    </div>
    <AutomationActionTeamMessageInput
      v-if="inputType === 'team_message'"
      v-model="action_params"
      :teams="dropdownValues"
    />
    <WootMessageEditor
      v-if="inputType === 'textarea'"
      v-model="castMessageVmodel"
      rows="4"
      enable-variables
      :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
      class="action-message"
    />
    <div v-if="isCustomAttributeAction && selectedAttribute" class="mt-2">
      <label class="text-xs text-n-text-subtle block mb-1">
        Value for {{ selectedAttribute.name }}
        <span class="text-n-text-subtlest">(supports {{ variables }})</span>
      </label>
      <input
        v-if="
          !selectedAttribute.attributeDisplayType ||
          selectedAttribute.attributeDisplayType === 'text' ||
          selectedAttribute.attributeDisplayType === 'link'
        "
        v-model="attributeValueInput"
        type="text"
        class="answer--text-input w-full"
        placeholder="Enter value (e.g., {{order_number}})"
      />
      <input
        v-else-if="selectedAttribute.attributeDisplayType === 'number'"
        v-model="attributeValueInput"
        type="text"
        class="answer--text-input w-full"
        placeholder="Enter number or variable (e.g., {{amount}})"
      />
      <select
        v-else-if="selectedAttribute.attributeDisplayType === 'list'"
        v-model="attributeValueInput"
        class="answer--text-input w-full"
      >
        <option value="">Select value or enter variable</option>
        <option
          v-for="option in selectedAttribute.attributeValues"
          :key="option"
          :value="option"
        >
          {{ option }}
        </option>
      </select>
      <input
        v-else-if="selectedAttribute.attributeDisplayType === 'date'"
        v-model="attributeValueInput"
        type="text"
        class="answer--text-input w-full"
        placeholder="Enter date or variable (e.g., {{payment_date}})"
      />
      <input
        v-else-if="selectedAttribute.attributeDisplayType === 'checkbox'"
        v-model="attributeValueInput"
        type="text"
        class="answer--text-input w-full"
        placeholder="Enter true/false or variable (e.g., {{is_paid}})"
      />
      <input
        v-else
        v-model="attributeValueInput"
        type="text"
        class="answer--text-input w-full"
        placeholder="Enter value (supports {{variables}})"
      />
    </div>
    <div v-if="isExecuteMacroAction && macroVariables.length > 0" class="mt-2">
      <div v-if="loadingVariables" class="text-xs text-n-text-subtle">
        Loading macro variables...
      </div>
      <div v-else>
        <label class="text-xs text-n-text-subtle block mb-2">
          This macro requires the following variables:
        </label>
        <div v-for="varName in macroVariables" :key="varName" class="mb-2">
          <label class="text-xs text-n-text-subtle block mb-1">
            {{ varName }}
          </label>
          <input
            :value="variableValues[varName]"
            type="text"
            class="answer--text-input w-full"
            :placeholder="`Enter value for {{${varName}}}`"
            @input="updateVariableValue(varName, $event.target.value)"
          />
        </div>
      </div>
    </div>
    <p v-if="errorMessage" class="filter-error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.interactive-message-trigger {
  margin-top: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-background);
}

.interactive-preview {
  margin-top: 1rem;
  padding: 0.75rem;
  background: var(--white);
  border: 1px solid var(--color-border);
  border-radius: 4px;
}

.interactive-preview p {
  margin: 0.25rem 0;
  color: var(--s-700);
}

.interactive-preview small {
  color: var(--s-500);
}
.filter {
  @apply bg-n-background p-2 border border-solid border-n-strong dark:border-n-strong rounded-lg mb-2;

  &.is-a-macro {
    @apply mb-0 bg-n-background dark:bg-n-solid-1 p-0 border-0 rounded-none;
  }
}

.no-margin-bottom {
  @apply mb-0;
}

.filter.has-error {
  @apply bg-n-ruby-8/20 border-n-ruby-5 dark:border-n-ruby-5;

  &.is-a-macro {
    @apply bg-transparent;
  }
}

.filter-inputs {
  @apply flex gap-1;
}

.filter-error {
  @apply text-n-ruby-9 dark:text-n-ruby-9 block my-1 mx-0;
}

.action__question,
.filter__operator {
  @apply mb-0 mr-1;
}

.action__question {
  @apply max-w-[50%];
}

.action__question.full-width {
  @apply max-w-full;
}

.filter__answer--wrap {
  @apply max-w-[50%] flex-grow mr-1 flex w-full items-center justify-start;

  input {
    @apply mb-0;
  }
}
.filter__answer {
  &.answer--text-input {
    @apply mb-0;
  }
}

.filter__join-operator-wrap {
  @apply relative z-20 m-0;
}

.filter__join-operator {
  @apply flex items-center justify-center relative my-2.5 mx-0;

  .operator__line {
    @apply absolute w-full border-b border-solid border-n-weak;
  }

  .operator__select {
    margin-bottom: 0 !important;
    @apply relative w-auto;
  }
}

.multiselect {
  @apply mb-0;
}
.action-message {
  @apply mt-2 mx-0 mb-0;
}
// Prosemirror does not have a native way of hiding the menu bar, hence
::v-deep .ProseMirror-menubar {
  @apply hidden;
}
</style>
